import { Button, Modal, Radio, Typography } from "antd"
import type { RadioChangeEvent } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ComparisonTable } from "../../DocumentSync/ComparisonTable.tsx"
import { mapFieldComparisonToGroupedValues } from "../../DocumentSync/grouped-comparison-adapter.ts"
import { formatRecommendedSourceSummary } from "../../DocumentSync/recommended-source-summary.ts"
import type {
  DocumentLabelInfo,
  FieldComparison,
  RecommendedSourceSummary
} from "../../DocumentSync/types.ts"

const { Text, Title } = Typography

type ConfirmMode = "recommended" | "lead"

type SourceKey = "lead" | "proposal" | "contract"

const SOURCE_COLUMNS: { key: SourceKey; labelKey: string }[] = [
  { key: "lead", labelKey: "leadColumn" },
  { key: "proposal", labelKey: "proposalColumn" },
  { key: "contract", labelKey: "contractColumn" }
]

interface ConfirmProjectInformationModalProps {
  open: boolean
  diffs: FieldComparison[]
  onCancel: () => void
  onConfirm: (applyFields?: Record<string, unknown>) => void
  confirmLoading?: boolean
  /** Optional: confirmed live only on `GET /crm/leads/{id}/sync-overview/`,
   * not (yet) on the lead-approval sync-preview endpoint this modal is
   * actually fed by — see `types.ts`. Falls back to the static translated
   * helper text when absent. */
  recommendedSourceSummary?: RecommendedSourceSummary
  /** Same live-confirmed caveat as above. */
  documentLabels?: { proposal?: DocumentLabelInfo; contract?: DocumentLabelInfo }
}

function displayFor(
  diff: FieldComparison,
  source: SourceKey,
  notSetLabel: string
): string {
  if (source === "lead") return diff.lead_display ?? "—"
  if (source === "proposal") return diff.proposal_display ?? notSetLabel
  return diff.contract_display ?? notSetLabel
}

/**
 * Shown when converting a Lead into a Project and the sync-preview endpoint
 * reports differences between the Lead, Proposal and Contract. Two choices —
 * the recommended (status-based) values, or the Lead's own values, an
 * equally valid pick, never a fallback — applied to every changed field at
 * once; no per-field selection. Never mounted when there are no diffs — that
 * case proceeds straight through with no UI.
 *
 * The comparison table renders every field the sync-preview response
 * carries (not only the ones that differ), so a user can see the full
 * picture of what the new Project will inherit; `has_diff` only decides
 * which rows get the differing-value emphasis, never whether a row is
 * rendered. Per-field recommendation captions were removed — the single
 * "Use recommended information" radio below the table says that once, for
 * every field, instead of repeating it on every row.
 */
function ConfirmProjectInformationModal({
  open,
  diffs,
  onCancel,
  onConfirm,
  confirmLoading,
  recommendedSourceSummary,
  documentLabels
}: ConfirmProjectInformationModalProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<ConfirmMode>("recommended")

  const changedDiffs = useMemo(
    () => diffs.filter((diff) => diff.has_diff),
    [diffs]
  )

  useEffect(() => {
    if (!open) return
    setMode("recommended")
  }, [open])

  const handleConfirm = () => {
    if (mode === "lead") {
      onConfirm(undefined)
      return
    }

    onConfirm(
      Object.fromEntries(
        changedDiffs.map((diff) => [diff.key, diff.recommended_value])
      )
    )
  }

  const notSetLabel = t("leadApproval.confirmProjectInfo.notSet")

  const tableColumns = SOURCE_COLUMNS.map((column) => {
    const dynamicLabel: string | undefined =
      column.key === "proposal"
        ? documentLabels?.proposal?.label
        : column.key === "contract"
          ? documentLabels?.contract?.label
          : undefined
    const label: string =
      dynamicLabel ?? t(`leadApproval.confirmProjectInfo.${column.labelKey}`)

    return { key: column.key, label }
  })

  const recommendedHelperText = recommendedSourceSummary
    ? formatRecommendedSourceSummary(recommendedSourceSummary)
    : t("leadApproval.confirmProjectInfo.useRecommendedHelper")

  const tableRows = diffs.map((diff) => {
    // Three columns share one row here, so there is no single "baseline vs
    // document being generated" pair the way the two Generate modals have —
    // `scope_diff`/`payment_terms_diff` can only ever describe one
    // direction. Attach it to whichever non-Lead column the backend itself
    // already recommends (computed from this same comparisons snapshot);
    // when Lead is the recommendation, no column gets the diff and the cell
    // still renders its collapsed summary + "View all", just without a
    // "View changes" affordance.
    const diffColumnKey =
      diff.recommended_source !== "lead" ? diff.recommended_source : undefined
    const grouped = mapFieldComparisonToGroupedValues(
      diff,
      ["lead", "proposal", "contract"],
      t,
      diffColumnKey
    )

    return {
      key: diff.key,
      label: diff.label,
      hasDiff: diff.has_diff,
      values: {
        lead: displayFor(diff, "lead", notSetLabel),
        proposal: displayFor(diff, "proposal", notSetLabel),
        contract: displayFor(diff, "contract", notSetLabel)
      },
      cellNotes: diff.contract_locked
        ? { contract: t("leadApproval.confirmProjectInfo.locked") }
        : undefined,
      groupedValues: grouped?.groupedValues,
      groupedCellCopy: grouped?.groupedCellCopy
    }
  })

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      centered
      width={720}
      // A Scope/Payment Terms row with many milestones/payments must never
      // grow the modal past a sane height — only this body scrolls; header
      // and footer stay pinned outside it (see ContractGenerateConfirmModal).
      styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {t("leadApproval.confirmProjectInfo.title")}
        </Title>
      }
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={confirmLoading}>
          {t("leadApproval.confirmProjectInfo.cancel")}
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={confirmLoading}
          onClick={handleConfirm}
        >
          {t("leadApproval.confirmProjectInfo.confirm")}
        </Button>
      ]}
    >
      <Text type="secondary">
        {t("leadApproval.confirmProjectInfo.description")}
      </Text>

      <div className="my-4">
        <ComparisonTable
          columns={tableColumns}
          rows={tableRows}
          testIdPrefix="confirm-diff"
          fieldColumnLabel={t("leadApproval.confirmProjectInfo.fieldColumn")}
        />
      </div>

      <Text className="block mb-2">
        {t("leadApproval.confirmProjectInfo.question")}
      </Text>

      <Radio.Group
        className="flex flex-col gap-3"
        value={mode}
        onChange={(e: RadioChangeEvent) => setMode(e.target.value as ConfirmMode)}
      >
        <Radio value="recommended" className="items-start">
          <div>
            <div>{t("leadApproval.confirmProjectInfo.useRecommended")}</div>
            <Text type="secondary" className="text-xs">
              {recommendedHelperText}
            </Text>
          </div>
        </Radio>
        <Radio value="lead" className="items-start">
          <div>
            <div>{t("leadApproval.confirmProjectInfo.useLead")}</div>
            <Text type="secondary" className="text-xs">
              {t("leadApproval.confirmProjectInfo.useLeadHelper")}
            </Text>
          </div>
        </Radio>
      </Radio.Group>
    </Modal>
  )
}

export { ConfirmProjectInformationModal }
